import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher } from "@/api/client";
import { ProductConfigurationRepository, ProductRepository } from "../domain/ProductRepository";
import { Product, AdditionalField, Field, ProductConfiguration } from "../domain/Product";
import { v4 as uuidv4 } from 'uuid';
import { Tax } from "../domain/Invoiceable";

function objectToDict(obj: any): Record<string, any> {
    const dict: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Recursively convert nested object
                dict[key] = objectToDict(value);
            } else {
                dict[key] = value;
            }
        }
    }

    return dict;
}

export class ApiProductRepository implements ProductRepository {

    command_dispatcher: CommandDispatcher
    async_command_dispatcher: CommandDispatcher
    query_dispatcher: QueryDispatcher
    constructor() {
        this.command_dispatcher = sync_command_dispatcher
        this.async_command_dispatcher = async_command_dispatcher
        this.query_dispatcher = query_dispatcher_get
    }

    api_to_domain(product) {

        let p =  new Product(
            product.id,
            product.code,
            product.description,
            product.price,
            product.company_id,
            product.taxes.map(tax => new Tax(tax.rate, tax.base, tax.amount, tax.recargo_rate, tax.recargo_amount)),
            product.deleted, 
            product.taxes_withheld.map(tax => new Tax(tax.rate, tax.base, tax.amount)),
            product.special_taxable_event
        );
        p.selected_tax = product.taxes[0].rate;
        p.selected_retention = 'na'
        if (product.taxes_withheld.length) {
            p.selected_retention = product.taxes_withheld[0].rate;
        }

        p.selected_tax_event = 'N/A'
        if (p.special_taxable_event){
            let code = p.special_taxable_event['code']
            if (code == '01'){
                if (p.special_taxable_event['reason'].includes('sujeto pasivo')){
                    p.selected_tax_event = 'ISP'
                }
                else if(p.special_taxable_event['reason'].includes('intracomunitaria')){
                    p.selected_tax_event = 'INTRA.'
                }
                else if(p.special_taxable_event['reason'].includes('exportaci')){
                    p.selected_tax_event = 'EXPORT.'
                }
                else if(p.special_taxable_event['reason'].includes('exent')){
                    p.selected_tax_event = 'EX'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 50')){
                    p.selected_tax_event = 'Art. 50'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 11')){
                    p.selected_tax_event = 'Art. 11'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 12')){
                    p.selected_tax_event = 'Art. 12'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 13')){
                    p.selected_tax_event = 'Art. 13'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 25')){
                    p.selected_tax_event = 'Art. 25'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 47')){
                    p.selected_tax_event = 'Art. 47'
                }
                else if(p.special_taxable_event['reason'].includes('Art. 110')){
                    p.selected_tax_event = 'Art. 110'
                }
                else if(p.special_taxable_event['reason'].includes('Otros')){
                    p.selected_tax_event = 'Otros'
                }

            }
            else if (code == '02'){
                p.selected_tax_event = 'NS'
            }
        }
        if (product.cost_price) p.cost_price = product.cost_price
        if (product.discount !== undefined) p.discount = product.discount
        if (product.discount_type) p.discount_type = product.discount_type
        if (product.additional_fields){
            p.additional_fields = product.additional_fields.map(field => new AdditionalField(field.name, field.key, field.value))
        }

        return p;
    }
    domain_to_api(product: Product) {
        return JSON.parse(JSON.stringify(objectToDict(product)));
    }

    async getProductList(company_id:Number, filters:Object): Promise<Product[]> {
        let args = { 
            company_id: company_id,
            filters: []
        };

        (Object.entries(filters) as [string, any][]).forEach(([key, value]) => {
            if(value.value) {
                args.filters.push({key: key, value: value.value})
            }
        })
        let productData = await this.query_dispatcher.dispatch('get.product.list', args)
        let productList: Product[] = []
        for (let inv in productData) {
            productList.push(this.api_to_domain(productData[inv]))
        }
        return productList
    }

    async save(product: Product) {
        let product_data = this.domain_to_api(product)
        let res = await this.command_dispatcher.dispatch('save.product', { product: product_data })
        return res['output']
    }

    create_empty_product(company_id, productConfiguration:ProductConfiguration) {
        let p = new Product(uuidv4(), '', '', 0.0, parseInt(company_id.toString()), [new Tax(0.21, 0, 0)], false)
        p.discount = 0
        p.discount_type = 'percentage'
        if (productConfiguration) {
            for (let field of productConfiguration.fields) {
                p.additional_fields.push(new AdditionalField(field.name, field.key, ""))
            }
        }
        return p
    }

    async delete(products: Product[]) {
        let args = {
            products: products.map(product => this.domain_to_api(product))
        }
        let res = await this.command_dispatcher.dispatch('delete.products', args)
        return res['output']
    }

    async upload_products_file(company_id, file_path, filename) {
        let args = {
            company_id: company_id,
            file_path: file_path,
            og_file_name: filename
          }
          let res = await this.async_command_dispatcher.dispatch_with_result('import.products.from.excel', args)
          return res
    }
}

export class ApiProductConfigurationRepository implements ProductConfigurationRepository {

    command_dispatcher: CommandDispatcher
    query_dispatcher: QueryDispatcher
    constructor() {
        this.command_dispatcher = sync_command_dispatcher
        this.query_dispatcher = query_dispatcher_get
    }

    api_to_domain(config) {
        let pc = new ProductConfiguration(
            config.company_id,
            config.fields.map((field: any) => new Field(field.name, field.key))
        )
        return pc
    }
    domain_to_api(config: ProductConfiguration) {
        return JSON.parse(JSON.stringify(objectToDict(config)));
    }

    async get_by_company(company_id): Promise<ProductConfiguration> {
        let configData = await this.query_dispatcher.dispatch('get.product.configuration', { company_id: company_id })
        if (configData) {
            return this.api_to_domain(configData)
        }
        else {
            return new ProductConfiguration(
                company_id,
                []
            )
        }
    }

    async save(configuration: ProductConfiguration) {
        let config_data = this.domain_to_api(configuration)
        let res = await this.command_dispatcher.dispatch('save.product.configuration', { product_configuration: config_data })
        return res['output']
    }
}